import { useEffect, useState, useContext } from 'react';
import PageGridItem from '../../Shared/Page/PageGridItem';
import Page, { PageContentBanner, PageContentBodyGrid } from '../../Shared/Page/Page';
import Table from '../../../components/Table/Table';
import { ChevronDown, ChevronUp } from 'react-feather';
import SearchTermFilter from '../../../components/Input/SearchTermFilter';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { pluralise, useRemote, useStorage } from '../../../Utils/Utils';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { BannerContext } from '../../../InternalApiApp';
import GroupingRow from './GroupingRow';
import { format, parseISO } from 'date-fns';

const COUNT_PREFIX = 'groupingCount_';
const VALUE_PREFIX = 'groupingValue_';

const PortfolioManagementList = ({ platform, product, grouping }) => {
	const [data, setData] = useState([]);
	const [supplementaryData, setSupplementaryData] = useState([]);
	const [filteredPortfolios, setFilteredPortfolios] = useState([]);
	const [hasSearchTerm, setHasSearchTerm] = useState(false);
	const { setBannerBreadCrumbs } = useContext(BannerContext);
	const [loading, setLoading] = useState(true);
	const [sortColumn, setSortColumn] = useState(null);
	const [sortDirection, setSortDirection] = useState('asc');
	const [hierarchy, setHierarchy] = useState([]);

	const remote = useRemote();
	const storage = useStorage();
	const location = useLocation();
	const history = useHistory();

	const listToFetch = platform ? "platform" : product ? "product" : grouping ? "grouping" : "portfolio";
	const listName = platform ? "Platforms" : product ? "Products" : grouping ? pluralise(grouping) : "Portfolios";

	useEffect(() => {
		storage.getOrFetch("funds/portfolio/groupings/hierarchy").then(res => {
			const names = (res || []).filter(item => item.displaysInHierarchy)
				.sort((a, b) => a.hierarchyOrder - b.hierarchyOrder)
				.map(item => item.groupingName);
			setHierarchy(names);
		})
	}, []);

	useEffect(() => {
		setData([])
		setFilteredPortfolios([])
		setLoading(true)

		let defaultSortColumn;
		switch (listToFetch) {
			case "platform": defaultSortColumn = 'schemeName'; break;
			case "product": defaultSortColumn = 'offerName'; break;
			case "grouping": defaultSortColumn = 'groupName'; break;
			default: defaultSortColumn = 'fundName'; break;
		}
		setSortColumn(defaultSortColumn);
		setSortDirection('asc');

		switch (listToFetch) {
			case "platform":
				setBannerBreadCrumbs({ tab: 'Platforms' })
				remote.get("funds/schemes").then(res => {
					setLoading(false)
					setData(res)
				});
				setSupplementaryData([]);
				break;
			case "product":
				setBannerBreadCrumbs({ tab: 'Products' })
				remote.get("funds/offers").then(res => {
					setLoading(false)
					setData(res)
				});
				setSupplementaryData([]);
				break;
			case "grouping":
				setBannerBreadCrumbs({ tab: listName })
				Promise.all([
					remote.get("funds/funds/groupings"),
					remote.get("funds/funds2")
				]).then(([groupingsRes, fundsRes]) => {
					const found = groupingsRes && groupingsRes.find(g => g.groupingName === grouping);
					const processed = found?.groups?.map(group => {
						const fundsList = group?.funds?.map(val => fundsRes.find(f => f.id === val));
						let productName = "";
						if (fundsList) {
							const offers = fundsList.map(f => f ? f.offerName : "");
							const unique = [...new Set(offers)];
							if (unique.length > 1) productName = "Multiple";
							else if (unique.length) productName = unique[0];
						}
						return { ...group, product: productName };
					}) || [];
					setData(processed);
					setSupplementaryData(fundsRes);
					setLoading(false);
				});
				break;
			default:
				setBannerBreadCrumbs({ tab: 'Portfolios' })
				remote.get("funds/funds2").then(res => {
					setLoading(false)
					setData(res)
				});
				setSupplementaryData([]);
				break;
		}
	}, [location]);

	const platformSortConfig = {
		schemeName: { type: 'string', label: 'Platform Name' },
		dateAdded: { type: 'string', label: 'Date Created' }
	};

	const productSortConfig = {
		offerName: { type: 'string', label: 'Product Name' },
		...Object.fromEntries(hierarchy.map(g => [COUNT_PREFIX + g, { type: 'number', label: pluralise(g) }])),
		portfolioCount: { type: 'number', label: 'Portfolios' }
	};

	const groupingSortConfig = {
		groupName: { type: 'string', label: (grouping || 'Group') + ' Name' },
		product: { type: 'string', label: 'Product' },
		portfolioCount: { type: 'number', label: 'Portfolios' }
	};

	const portfolioSortConfig = {
		fundName: { type: 'string', label: 'Portfolio Name' },
		fundNumber: { type: 'string', label: 'Code' },
		offerName: { type: 'string', label: 'Product' },
		...Object.fromEntries(hierarchy.map(g => [VALUE_PREFIX + g, { type: 'string', label: g }]))
	};

	const getSortConfig = () => {
		switch (listToFetch) {
			case "platform": return platformSortConfig;
			case "product": return productSortConfig;
			case "grouping": return groupingSortConfig;
			default: return portfolioSortConfig;
		}
	};

	const handleSort = (column) => {
		if (sortColumn === column) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortColumn(column);
			setSortDirection('asc');
		}
	};

	const SortableHeader = ({ column, config }) => {
		const isActive = sortColumn === column;
		const ChevronIcon = sortDirection === 'asc' ? ChevronUp : ChevronDown;
		const color = isActive ? "#0e8efd" : "#c2c4ce";
		return (
			<div className="sorting-header cursor-pointer" onClick={() => handleSort(column)}>
				{config.label} {isActive && <ChevronIcon size={16} color={color} />}
			</div>
		);
	};

	const generateHeaders = () => {
		const sortConfig = getSortConfig();
		return Object.keys(sortConfig).map(column => ({
			text: <SortableHeader column={column} config={sortConfig[column]} />
		}));
	};

	const headers = generateHeaders();

	const colWidths =
		platform ? null
			: product ? [{ minWidth: "300px", width: "600px" }, ...hierarchy.map(() => ({ minWidth: "150px" })), { minWidth: "150px" }]
				: grouping ? [{ minWidth: "300px", width: "600px" }, { minWidth: "200px", width: "300px" }, { minWidth: "150px" }, { minWidth: "100px", maxWidth: "150px" }]
					: [{ minWidth: "300px", width: "500px" }, { minWidth: "150px", maxWidth: "150px" }, { minWidth: "200px" }, ...hierarchy.map(() => ({ minWidth: "200px" }))]

	const getCellValue = (obj, column) => {
		if (column.startsWith(COUNT_PREFIX)) {
			const key = column.slice(COUNT_PREFIX.length);
			return obj.groupings?.[key]?.length || 0;
		}
		if (column.startsWith(VALUE_PREFIX)) {
			const key = column.slice(VALUE_PREFIX.length);
			const v = obj.groupings?.[key];
			return typeof v === 'string' ? v : '';
		}
		if (column === 'portfolioCount') {
			return obj.funds?.length || 0;
		}
		return obj[column] ?? '';
	};

	const dataMap = (dataToMap) => {
		let sortedData = dataToMap?.slice();
		const sortConfig = getSortConfig();

		if (sortColumn && sortedData) {
			sortedData.sort((a, b) => {
				const aValue = getCellValue(a, sortColumn);
				const bValue = getCellValue(b, sortColumn);
				const sortType = sortConfig[sortColumn]?.type || 'string';
				if (sortType === 'string') {
					const result = String(aValue).localeCompare(String(bValue));
					return sortDirection === 'asc' ? result : -result;
				} else if (sortType === 'number') {
					const result = Number(aValue) - Number(bValue);
					return sortDirection === 'asc' ? result : -result;
				}
				return 0;
			});
		} else {
			// Existing default sorting logic when no column is selected
			sortedData = sortedData?.sort((a, b) => {
				switch (listToFetch) {
					case "platform":
						if (!a.schemeName) return 1;
						if (!b.schemeName) return -1;
						return a.schemeName.localeCompare(b.schemeName);
					case "product":
						if (!a.offerName) return 1;
						if (!b.offerName) return -1;
						return a.offerName.localeCompare(b.offerName);
					case "manager":
					case "practice":
						if (a.scope === b.scope) {
							if (!a.groupName) return 1;
							if (!b.groupName) return -1;
							return a.groupName.localeCompare(b.groupName);
						}
						if (!a.scope) return 1;
						if (!b.scope) return -1;
						return a.scope.localeCompare(b.scope);
					case "portfolio":
						if (!a.fundName) return 1;
						if (!b.fundName) return -1;
						return a.fundName.localeCompare(b.fundName);
					default:
						return 0;
				}
			});
		}

		return sortedData?.map((obj, idx) => {
			switch (listToFetch) {
				case "platform":
					return (
						<tr className="cursor-pointer" key={idx} onClick={() => history.push("/admin/portfolio-management/platforms/" + obj.schemeNumber)}>
							<td><p>{obj.schemeName}</p></td>
							<td><p>{obj.dateAdded && format(parseISO(obj.dateAdded), "dd/MM/yyyy")}</p></td>
						</tr>
					)
				case "product":
					return (
						<tr className="cursor-pointer" key={idx} onClick={() => history.push("/admin/portfolio-management/products/" + obj.offerNumber)}>
							<td><p>{obj.offerName}</p></td>
							{hierarchy.map(g => (
								<td key={g}><p>{obj.groupings?.[g]?.length || 0}</p></td>
							))}
							<td><p>{obj?.funds?.length || "-"}</p></td>
						</tr>
					)
				case "grouping":
					return (
						<GroupingRow
							key={JSON.stringify(obj) + idx}
							group={obj}
							funds={supplementaryData}
							idx={idx}
							grouping={grouping}
							hierarchy={hierarchy}
						/>
					)
				case "portfolio":
					const badgedPractice = !!(obj?.cloneOf);
					return (
						<tr className="cursor-pointer" key={idx} onClick={() => history.push("/admin/portfolio-management/portfolios/" + obj.fundNumber)}>
							<td><p>
								{obj.fundName ? obj.fundName : "-"}
								{badgedPractice && <i className='material-symbols-outlined' title="Badged Practice" style={{ verticalAlign: 'middle', color: '#06467D'}}>license</i>}
							</p></td>
							<td><p>{obj.fundNumber ? obj.fundNumber : "-"}</p></td>
							<td><p>{obj.offerName ? obj.offerName : "-"}</p></td>
							<td><p>{obj.groupings?.Practice && typeof obj.groupings.Practice === "string" ? obj.groupings.Practice : "-"}</p></td>
							<td><p>{obj.groupings?.Manager && typeof obj.groupings.Manager === "string" ? obj.groupings.Manager : "-"}</p></td>
							{/* <td><p>{obj.dateStarted ? format(parseISO(obj.dateStarted), "dd/MM/yyyy") : "-"}</p></td> */}
						</tr>
					)

				default:
					return (
						<tr className="cursor-pointer" key={idx} onClick={() => history.push("/admin/portfolio-management/portfolios/" + obj.fundNumber)}>
							<td><p>{obj.fundName || "-"}</p></td>
							<td><p>{obj.fundNumber || "-"}</p></td>
							<td><p>{obj.offerName || "-"}</p></td>
							{hierarchy.map(g => (
								<td key={g}><p>{typeof obj.groupings?.[g] === "string" ? obj.groupings[g] : "-"}</p></td>
							))}
						</tr>
					)
			}
		})
	}

	const searchPaths = [
		"fundName", "fundNumber", "schemeName", "schemeNumber",
		"offerName", "offerNumber", "groupName", "product",
		...hierarchy.map(g => `groupings.${g}`)
	];

	return (
		<Page fixedBanner>
			<PageContentBanner divider align="center" gap="l" style={{ paddingTop: "0px" }}>
				<SearchTermFilter
					size="small"
					width="l"
					placeholder={`Search`}
					isClearable
					dataSet={data}
					setFilteredData={setFilteredPortfolios}
					path={searchPaths}
					hasSearchTerm={(boolean) => setHasSearchTerm(boolean)}
				/>
				<p className='font-weight-medium p-medium mb-none'>{data && data.length ? `Total ${listName}: ${data?.length}` : ""}</p>
			</PageContentBanner>
			<PageContentBodyGrid rowGap="xl" showScrollbar>
				<PageGridItem container col="1 / span 12">
					<div>
						<Table
							minWidth={850}
							tableStyle={{ maxWidth: 1200 }}
							dataLoading={loading}
							headers={headers}
							dataMap={dataMap(hasSearchTerm ? filteredPortfolios : data)}
							colWidths={colWidths}
						/>
					</div>
				</PageGridItem>
			</PageContentBodyGrid>
		</Page>
	);
}

export default PortfolioManagementList;
